import * as React from 'react';
import * as c from 'models/constants';
import { Button, ErrorSummary, Modal } from 'components';
import { translate } from 'react-i18next';
import { ITranslateProps } from 'models/common';
import { IOffice } from 'models/shop/office';
import { connect, Dispatch } from 'react-redux';
import { IStore } from 'redux/IStore';
import {
	applyShopServiceTemplate,
	deleteShopServiceTemplate,
	getServiceTemplateDetails,
	getServiceTemplatesByShop,
} from 'redux/modules/shop/servicetemplates';
import { IShopCard } from 'models/shop/shopcard';
import { LoadingOverlayWrapper } from 'components/Loader';
import { IShopServiceTemplate } from 'models/shop/serviceTemplates';
import { getShopCard } from 'redux/modules/shop/shopcard';
import { TemplateReview } from '../TemplateReview';
import { serviceTemplateToShopServiceTree } from 'helpers/bookingTemplates';
import { PortalPageSection } from 'components/PortalStructure';
import renderIcon from 'helpers/renderIcon';
import { BookingInputField } from 'components/FormControls';

interface ILoadShopServiceTemplateOwnProps {
	shopId: IShopCard['id'];
	officeId: IOffice['id'];
	closeModal: () => void;
}

interface IState {
	step: 'template' | 'review' | 'delete' | 'confirm';
	templateName?: IShopServiceTemplate['templateName'];
	templateOfficeId?: string;
	chosenServiceCategoryId: string;
	chosenTemplates: string[];
	inputValue: string;

	// for scroll
	categoriesNotScrolled: boolean;
	categoriesScrolledToTheEnd: boolean;
}

let scrollTimeout;

interface IProps
	extends ILoadShopServiceTemplateOwnProps,
		ITranslateProps,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class LoadShopServiceTemplateModal extends React.Component<IProps, IState> {
	private serviceCategoriesRef;

	public constructor(props: IProps) {
		super(props);

		this.serviceCategoriesRef = React.createRef();

		this.state = {
			step: 'template',
			chosenServiceCategoryId: null,
			chosenTemplates: [],
			inputValue: '',
			categoriesNotScrolled: true,
			categoriesScrolledToTheEnd: false,
		};

		this.proceed = this.proceed.bind(this);
		this.handleSelectTemplate = this.handleSelectTemplate.bind(this);
		this.handleTemplateDelete = this.handleTemplateDelete.bind(this);
		this.handleSelectChosenTemplateClick = this.handleSelectChosenTemplateClick.bind(this);
		this.handleServiceCategorySelect = this.handleServiceCategorySelect.bind(this);
		this.handleChange = this.handleChange.bind(this);
		this.handleServiceCategorySelectAll = this.handleServiceCategorySelectAll.bind(this);
		this.handleServiceCategoriesScroll = this.handleServiceCategoriesScroll.bind(this);
		this.scrollCategories = this.scrollCategories.bind(this);
	}

	public componentDidMount(): void {
		this.props.loadTemplates();

		if (!this.props.fetchingOffices && !this.props.offices.length) {
			this.props.loadOffices();
		}
	}

	public componentDidUpdate(_: Readonly<IProps>, prevState: Readonly<IState>): void {
		if (prevState.step !== this.state.step && this.state.step === 'review') {
			this.props.loadTemplateDetails(this.state.templateName).then(() => {
				const template = this.props.templates.find((t) => t.templateName === this.state.templateName);
				const shopServiceTree = serviceTemplateToShopServiceTree(template);

				if (template && template?.templateItems?.length) {
					// Hide bundles for now (requires a rework in calls > save children first > grab newly created children id's and add to new bundle)
					const templateItemsWithoutBundles = template?.templateItems
						.filter((ti) => !(ti?.customData && JSON.parse(ti.customData).isBundle))
						.map((tItems) => tItems.id);

					this.setState({
						chosenTemplates: templateItemsWithoutBundles,
						chosenServiceCategoryId: shopServiceTree[0].id,
					});
				}

				this.serviceCategoriesRef.current.addEventListener('scroll', () => {
					clearTimeout(scrollTimeout);
					scrollTimeout = setTimeout(() => {
						this.handleServiceCategoriesScroll();
					}, 50);
				});

				this.handleServiceCategoriesScroll();
			});
		}
	}

	public componentWillUnmount() {
		if (this.serviceCategoriesRef?.current) {
			this.serviceCategoriesRef.current.removeEventListener('scroll', () => {
				clearTimeout(scrollTimeout);
				scrollTimeout = setTimeout(() => {
					this.handleServiceCategoriesScroll();
				}, 50);
			});
		}
	}

	private get isValid(): boolean {
		const { step, templateName } = this.state;
		if (!templateName) {
			return false;
		}
		const validNames = (this.props.templates || []).map((t) => t.templateName);
		if (!validNames.includes(templateName)) {
			return false;
		}
		if (step === 'template') {
			return true;
		}
		return this.props.officeId ? true : false;
	}

	private save() {
		this.props.applyTemplate(this.state.templateName, this.state.chosenTemplates);
	}

	private delete() {
		this.props.deleteTemplate(this.state.templateName, this.state.templateOfficeId);
	}

	private proceed() {
		const { services, templates } = this.props;
		const serviceOfficeIds = (services || []).map((s) => s.officeId);
		const { step } = this.state;

		if (!this.isValid) {
			return;
		}

		if (step === 'template') {
			const template = templates.find((t) => t.templateName === this.state.templateName);

			if (template && template?.templateItems?.length) {
				this.setState({ chosenTemplates: template?.templateItems.map((ti) => ti.id) });
			}

			return this.setState({ step: 'review' });
		}

		if (step === 'review') {
			const existing = serviceOfficeIds.includes(this.props.officeId);

			if (existing) {
				return this.setState({ step: 'confirm' });
			} else {
				return this.save();
			}
		}

		if (step === 'confirm') {
			return this.save();
		}
	}

	private handleSelectTemplate(templateName: string) {
		this.setState({ templateName });
		setTimeout(() => {
			this.proceed();
		}, 50);
	}

	private handleTemplateDelete(e, templateName, templateOfficeId) {
		e.stopPropagation();
		this.setState({ step: 'delete', templateName, templateOfficeId });
		setTimeout(() => {
			this.proceed();
		}, 50);
	}

	private handleSelectChosenTemplateClick(itemId: string) {
		const tempChosenTemplates = [...this.state.chosenTemplates];

		if (tempChosenTemplates.includes(itemId)) {
			tempChosenTemplates.splice(tempChosenTemplates.indexOf(itemId), 1);
		} else {
			tempChosenTemplates.push(itemId);
		}

		this.setState({ chosenTemplates: tempChosenTemplates });
	}

	private handleServiceCategorySelectAll() {
		const { chosenServiceCategoryId, chosenTemplates } = this.state;
		const { templates } = this.props;

		const currentTemplate = templates.find((tem) => tem.templateName === this.state.templateName);
		const currentServiceCategoryServices = [];
		const includedServices = [];
		const missingTemplates = [];
		let currentActiveServicesInCategory = 0;

		// Save current service category services into an array
		currentTemplate?.templateItems.forEach((ti) => {
			if (ti.serviceCategoryId.toString() === chosenServiceCategoryId) {
				currentServiceCategoryServices.push(ti);
			}
		});

		// Compare if current service category services are selected
		for (const cscs of currentServiceCategoryServices) {
			const id = cscs.id.toString();

			if (chosenTemplates.includes(id)) {
				currentActiveServicesInCategory += 1;
				includedServices.push(cscs.id.toString());
			} else {
				missingTemplates.push(cscs.id.toString());
			}
		}

		// Set chosenTemplates state depending on if all services were selected or not
		if (currentServiceCategoryServices.length === currentActiveServicesInCategory) {
			const newChosenTemplates = chosenTemplates.filter((id) => !includedServices.includes(id));
			this.setState({ chosenTemplates: newChosenTemplates });
		} else {
			this.setState({ chosenTemplates: [...chosenTemplates, ...missingTemplates] });
		}
	}

	private handleServiceCategorySelect(categoryId: string) {
		this.setState({ chosenServiceCategoryId: categoryId });
	}

	private handleChange(event) {
		this.setState({ inputValue: event.target.value });
	}

	private handleServiceCategoriesScroll() {
		const serviceCategoriesContainer = this.serviceCategoriesRef.current;
		if (serviceCategoriesContainer) {
			this.setState({
				categoriesNotScrolled: serviceCategoriesContainer.scrollLeft === 0,
				categoriesScrolledToTheEnd:
					Math.abs(serviceCategoriesContainer.scrollLeft) >=
					serviceCategoriesContainer.scrollWidth - serviceCategoriesContainer.clientWidth,
			});
		}
	}

	private scrollCategories(direction: 'left' | 'right') {
		const serviceCategoriesContainer = this.serviceCategoriesRef.current;

		if (serviceCategoriesContainer && direction) {
			const pxToScrollOnClick = 240;

			if (direction === 'right') {
				serviceCategoriesContainer.scroll({
					left: pxToScrollOnClick + serviceCategoriesContainer.scrollLeft,
					behavior: 'smooth',
				});
			}
			if (direction === 'left') {
				serviceCategoriesContainer.scroll({
					left: serviceCategoriesContainer.scrollLeft - pxToScrollOnClick,
					behavior: 'smooth',
				});
			}
		}
	}

	public renderTemplates(): JSX.Element {
		const { templates } = this.props;
		const options = (templates || []).map((tem) => (
			<React.Fragment key={tem.templateName}>
				{/* <PortalPageSection onClick={() => this.handleSelectTemplate(t.templateName)} className={`${t.templateName === this.state.templateName ? 'active' : ''}`}> */}
				<PortalPageSection onClick={() => this.handleSelectTemplate(tem.templateName)}>
					{tem.templateName}
					{renderIcon('delete', 16, 16, 'deleteTemplateButton', (e) => {
						this.handleTemplateDelete(e, tem.templateName, tem.officeId);
					})}
				</PortalPageSection>
			</React.Fragment>
		));
		return (
			<div>
				<div className="loadTemplateModal__headerContainer">
					<h1 className="loadTemplateModal__title">{this.modalTitle}</h1>
					<div className="loadTemplateModal__closeButton" onClick={this.props.closeModal}>
						{renderIcon('cancel', 20, 20)}
					</div>
				</div>
				{options}
			</div>
		);
	}

	public renderReview(): JSX.Element {
		const { templates, t } = this.props;
		const template = templates.find((tem) => tem.templateName === this.state.templateName);
		const shopServiceTree = serviceTemplateToShopServiceTree(template);
		const currentServiceCategoryServices = [];

		// Take only currently selected service category's services
		const currentServiceTree = [];

		if (this.state.chosenServiceCategoryId) {
			template?.templateItems.forEach((ti) => {
				if (
					ti.serviceCategoryId.toString() === this.state.chosenServiceCategoryId &&
					!(ti?.customData && JSON.parse(ti.customData).isBundle) // Hide bundles for now
				) {
					currentServiceCategoryServices.push(ti);
				}
			});

			currentServiceTree.push(shopServiceTree.find((sst) => sst.id === this.state.chosenServiceCategoryId));
		}

		if (!template) {
			return null;
		}

		// Group service groups services
		const groupedServiceGroupsTemplates = currentServiceCategoryServices.reduce((groups, item) => {
			const parentServiceGroupId = item.parentServiceGroupId;

			const existingGroup = groups.find((group) => group.parentServiceGroupId === parentServiceGroupId);
			if (existingGroup) {
				existingGroup.items.push(item);
			} else {
				groups.push({ parentServiceGroupId: parentServiceGroupId, items: [item] });
			}

			return groups;
		}, []);

		return (
			<>
				<div className="loadTemplateModal__headerContainer">
					<div className="loadTemplateModal__titleContainer">
						{renderIcon('arrow_left', 18, 18, 'loadTemplateModal__backButton', () => {
							this.setState({ step: 'template' });
						})}
						<h1 className="loadTemplateModal__title">{this.modalTitle}</h1>
					</div>

					<div className="loadTemplateModal__closeButton" onClick={this.props.closeModal}>
						{renderIcon('cancel', 20, 20)}
					</div>
				</div>

				<div className="loadTemplateModal__textField">
					<BookingInputField
						className="loadTemplateModal__textField__serviceName"
						label={'Search services'}
						placeholder={'Search services'}
						customIconName={'search1'}
						customIconClassName={'servicesSearchIcon'}
						onChangeAction={(e) => this.handleChange(e)}
						input={{ value: this.state.inputValue }}
					/>
				</div>

				<div className="loadTemplateModal__serviceCategoriesContainer">
					{!this.state.categoriesNotScrolled && (
						<div className="loadTemplateModal__scrollLeft" onClick={() => this.scrollCategories('left')}>
							{renderIcon('roundLeft')}
						</div>
					)}

					<div
						className={`loadTemplateModal__serviceCategories ${
							this.state.categoriesScrolledToTheEnd && this.state.categoriesNotScrolled ? 'noScroll' : ''
						}`}
						ref={this.serviceCategoriesRef}
					>
						{shopServiceTree.map((sst) => {
							return (
								<span
									key={sst.id}
									className={`loadTemplateModal__serviceCategory ${
										this.state.chosenServiceCategoryId === sst.id ? 'active' : ''
									}`}
									onClick={() => this.handleServiceCategorySelect(sst.id)}
								>
									{sst.name}
								</span>
							);
						})}
					</div>

					{!this.state.categoriesScrolledToTheEnd && (
						<div className="loadTemplateModal__scrollRight" onClick={() => this.scrollCategories('right')}>
							{renderIcon('roundRight')}
						</div>
					)}
				</div>

				<TemplateReview
					items={currentServiceTree}
					handleSelectChosenTemplateClick={(itemId: string) => this.handleSelectChosenTemplateClick(itemId)}
					handleServiceCategorySelectAll={() => this.handleServiceCategorySelectAll()}
					currencyKey={this.props?.currencyKey}
					chosenTemplates={this.state.chosenTemplates}
					currentServiceCategoryServices={currentServiceCategoryServices}
					groupedServiceGroupsTemplates={groupedServiceGroupsTemplates}
					t={t}
					inputValue={this.state.inputValue}
				/>
			</>
		);
	}

	public renderConfirm(): JSX.Element {
		const { t } = this.props;
		return (
			<div>
				<div className="loadTemplateModal__headerContainer">
					<div className="loadTemplateModal__titleContainer">
						{renderIcon('arrow_left', 18, 18, 'loadTemplateModal__backButton', () => {
							this.setState({ step: 'review' });
						})}
						<h1 className="loadTemplateModal__title">{this.modalTitle}</h1>
					</div>

					<div className="loadTemplateModal__closeButton" onClick={this.props.closeModal}>
						{renderIcon('cancel', 20, 20)}
					</div>
				</div>
				<p className="muted">{t('User_ShopCard_Services_LoadTemplate_Warning')}</p>
			</div>
		);
	}

	public renderDelete(): JSX.Element {
		const { t } = this.props;

		return (
			<div>
				<div className="loadTemplateModal__headerContainer">
					<div className="loadTemplateModal__titleContainer">
						{renderIcon('arrow_left', 18, 18, 'loadTemplateModal__backButton', () => {
							this.setState({ step: 'template' });
						})}
						<h1 className="loadTemplateModal__title">{this.modalTitle}</h1>
					</div>

					<div className="loadTemplateModal__closeButton" onClick={this.props.closeModal}>
						{renderIcon('cancel', 20, 20)}
					</div>
				</div>
				<p className="muted">{t('User_ShopCard_Services_DeleteTheTemplate')}</p>
			</div>
		);
	}

	public renderModalFooter(): JSX.Element {
		const { t } = this.props;
		if (this.state.step === 'template') {
			return null;
		} else if (this.state.step === 'delete') {
			return (
				<div className="modalButtonContainer">
					<span
						key="cancel"
						className="modalButtonContainer__cancelButton"
						onClick={() => {
							this.setState({ step: 'template' });
						}}
					>
						{t('BSPortal_Common_Back')}
					</span>
					<Button
						key="delete"
						onClick={() => {
							this.delete();
						}}
					>
						{t('BSPortal_Common_Delete')}
					</Button>
				</div>
			);
		} else {
			return (
				<div className="modalButtonContainer">
					<span
						key="cancel"
						className="modalButtonContainer__cancelButton"
						onClick={() => {
							this.setState({ step: this.state.step === 'confirm' ? 'review' : 'template' });
						}}
					>
						{t('BSPortal_Common_Back')}
					</span>
					<Button key="save" disabled={!this.isValid} onClick={this.proceed}>
						{t('BSPortal_Common_Apply')}
					</Button>
				</div>
			);
		}
	}

	private renderModal(): JSX.Element {
		switch (this.state.step) {
			case 'template':
				return this.renderTemplates();
			case 'review':
				return this.renderReview();
			case 'delete':
				return this.renderDelete();
			case 'confirm':
				return this.renderConfirm();
			default:
				return null;
		}
	}

	private get modalTitle(): string {
		const { t } = this.props;
		switch (this.state.step) {
			case 'template':
				return t('User_ShopCard_Services_LoadTemplate_Title');
			case 'review':
				return this.state.templateName;
			case 'confirm':
				return t('BSPortal_Common_ConfirmationDialog_Sure');
			case 'delete':
				return t('User_ShopCard_Services_DeleteTemplate_Title');
			default:
				return t('User_ShopCard_Services_SaveAsTemplate_Title');
		}
	}

	public render() {
		const { isFetching, fetchingOffices, closeModal, error, message } = this.props;

		return (
			<Modal
				modalClass={`modal-with-form card loadTemplateModal ${this.state.step === 'review' ? 'reviewStep' : ''}`}
				size="small"
				closeModal={closeModal}
				footer={this.renderModalFooter()}
			>
				<ErrorSummary error={error} message={message} />
				<LoadingOverlayWrapper active={isFetching || fetchingOffices}>{this.renderModal()}</LoadingOverlayWrapper>
			</Modal>
		);
	}
}

const mapStateToProps = (state: IStore, ownProps: ILoadShopServiceTemplateOwnProps) => ({
	templates: state.shopServiceTemplates.shopServiceTemplates,
	isFetching: state.shopServiceTemplates.isFetching,
	offices: (state.shopcard.shopcard?.offices || []).filter((o) => o.shopId === ownProps.shopId),
	currencyKey: state.shopcard.shopcard?.currencyKey,
	fetchingOffices: state.shopcard.isFetching,
	services: (state.shopBookingTemplates.services || []).filter((s) => s.shopId === ownProps.shopId),
	error: state.shopServiceTemplates.error,
	message: state.shopServiceTemplates.message,
});

const mapDispatchToProps = (dispatch: Dispatch<IStore>, ownProps: ILoadShopServiceTemplateOwnProps) => ({
	loadTemplates: () => dispatch(getServiceTemplatesByShop(ownProps.shopId)),
	loadTemplateDetails: (name: string) => dispatch(getServiceTemplateDetails(name)),
	loadOffices: () => dispatch(getShopCard(ownProps.shopId, false, true)),
	applyTemplate: (templateName: IState['templateName'], chosenTemplates) =>
		dispatch(applyShopServiceTemplate(templateName, ownProps.officeId, chosenTemplates)).then((success) =>
			success ? ownProps.closeModal() : null,
		),
	deleteTemplate: (templateName, templateOfficeId) =>
		dispatch(deleteShopServiceTemplate(templateName, templateOfficeId)).then((success) =>
			success ? ownProps.closeModal() : null,
		),
});

const LoadShopServiceTemplate = connect(mapStateToProps, mapDispatchToProps)(LoadShopServiceTemplateModal);

export { LoadShopServiceTemplate, ILoadShopServiceTemplateOwnProps };
